/**
 * Monitor signal configuration for the "Create Monitor with Dataset" flow.
 * Defines available signals organized by category with default queries and criteria.
 */

import type { EventCategory, EventTypeOption } from '../types/monitoring';

/**
 * Signal options organized by category
 */
export const EVENT_OPTIONS: Record<Exclude<EventCategory, 'custom'>, EventTypeOption[]> = {
  people: [
    {
      type: 'job_change',
      label: 'Job change',
      icon: 'Briefcase',
      category: 'people',
      defaultCriterion: 'changed jobs recently',
      defaultQuery: 'professionals who recently changed jobs',
    },
    {
      type: 'promotion',
      label: 'Promotion',
      icon: 'TrendingUp',
      category: 'people',
      defaultCriterion: 'recently promoted',
      defaultQuery: 'professionals who were recently promoted',
    },
    {
      type: 'location_change',
      label: 'Location change',
      icon: 'MapPin',
      category: 'people',
      defaultCriterion: 'recently relocated',
      defaultQuery: 'professionals who relocated',
    },
    {
      type: 'social_media_post',
      label: 'Social media post',
      icon: 'MessageSquare',
      category: 'people',
      defaultCriterion: 'active on social media',
      defaultQuery: 'professionals active on social media',
    },
  ],
  companies: [
    {
      type: 'new_hire',
      label: 'New hire',
      icon: 'Users',
      category: 'companies',
      defaultCriterion: 'actively hiring',
      defaultQuery: 'companies that are actively hiring',
    },
    {
      type: 'funding_round',
      label: 'Funding round',
      icon: 'DollarSign',
      category: 'companies',
      defaultCriterion: 'raised funding recently',
      defaultQuery: 'companies that recently raised funding',
    },
    {
      type: 'product_launch',
      label: 'Product launch',
      icon: 'Rocket',
      category: 'companies',
      defaultCriterion: 'launched a new product recently',
      defaultQuery: 'companies that recently launched new products',
    },
    {
      type: 'partnership',
      label: 'Partnership',
      icon: 'Handshake',
      category: 'companies',
      defaultCriterion: 'announced a partnership recently',
      defaultQuery: 'companies that announced partnerships',
    },
    {
      type: 'layoff',
      label: 'Layoff',
      icon: 'TrendingDown',
      category: 'companies',
      defaultCriterion: 'announced layoffs recently',
      defaultQuery: 'companies that announced layoffs',
    },
    {
      type: 'acquisition',
      label: 'Acquisition',
      icon: 'Link',
      category: 'companies',
      defaultCriterion: 'involved in an acquisition recently',
      defaultQuery: 'companies involved in acquisitions',
    },
  ],
  news: [
    {
      type: 'new_research_publication',
      label: 'Research publication',
      icon: 'FileText',
      category: 'news',
      defaultCriterion: 'published new research',
      defaultQuery: 'new research publications',
    },
    {
      type: 'movie_trailer',
      label: 'Movie trailer',
      icon: 'Film',
      category: 'news',
      defaultCriterion: 'new movie trailer released',
      defaultQuery: 'new movie trailers',
    },
    {
      type: 'game_announcement',
      label: 'Game announcement',
      icon: 'Gamepad2',
      category: 'news',
      defaultCriterion: 'video game announced',
      defaultQuery: 'video game announcements',
    },
    {
      type: 'concert',
      label: 'Concert',
      icon: 'Music',
      category: 'news',
      defaultCriterion: 'concert announced',
      defaultQuery: 'upcoming concerts',
    },
    {
      type: 'music_release',
      label: 'Music release',
      icon: 'Headphones',
      category: 'news',
      defaultCriterion: 'new music released',
      defaultQuery: 'new music releases',
    },
  ],
};

/**
 * Maximum number of criteria allowed (Exa limit)
 */
export const MAX_CRITERIA = 5;

/**
 * Default max results for monitors created from scratch
 */
export const DEFAULT_MAX_RESULTS = 10;

/**
 * Available max results preset options (custom also available with max 200)
 */
export const MAX_RESULTS_OPTIONS = [10, 25] as const;

/**
 * Maximum allowed results per run
 */
export const MAX_RESULTS_LIMIT = 200;

/**
 * Category labels for UI display
 */
export const CATEGORY_LABELS: Record<Exclude<EventCategory, 'custom'>, string> = {
  people: 'People',
  companies: 'Companies',
  news: 'News',
};

/**
 * Find an event option by type across all categories
 */
export function findEventOption(eventType: string): EventTypeOption | undefined {
  for (const category of Object.values(EVENT_OPTIONS)) {
    const option = category.find((opt) => opt.type === eventType);
    if (option) return option;
  }
  return undefined;
}
